'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import type {
  CSSProperties,
  HTMLAttributes,
  PointerEvent as ReactPointerEvent,
  MouseEvent as ReactMouseEvent,
  ReactNode,
} from 'react';
import { Eyebrow } from './Eyebrow';
import { Title } from './Title';
import { cx } from '../utils';
import type { UnsafeProps } from '../escape-hatches';
import { useStableId } from '../hooks/useStableId';
import { tabbables } from '../internal/focusTrap';
import { acquireScrollLock, releaseScrollLock } from '../internal/scrollLock';
import { acquireSiblingInert, releaseSiblingInert } from '../internal/modalInert';
import { pushModal, popModal, isTopmost } from '../internal/modalStack';
import { devWarn } from '../internal/devWarn';
import { useSheetDrag } from '../internal/useSheetDrag';
import { pickAllowed } from '../internal/pickAllowed';
import { JIcon } from '../../icons';

type ModalAllowedRest = Pick<HTMLAttributes<HTMLDivElement>, 'id' | 'lang' | 'dir'> & {
  [key: `data-${string}`]: string | number | boolean | undefined;
  [key: `aria-${string}`]: string | boolean | number | undefined;
};

export type ModalPresentation = 'dialog' | 'bottom-sheet';
export type ModalResponsivePresentation =
  | ModalPresentation
  | {
      mobile?: ModalPresentation;
      desktop?: 'dialog';
    };

export interface ModalProps extends UnsafeProps, ModalAllowedRest {
  open: boolean;
  onClose?: () => void;
  children?: ReactNode;
  width?: number | string;
  title?: ReactNode;
  kicker?: ReactNode;
  headerless?: boolean;
  ariaLabel?: string;
  /* Optional id of the element describing the dialog content.
     APG AlertDialog pattern strongly recommends this so AT users
     hear the consequence + the action options together. */
  ariaDescribedBy?: string;
  /* Selects between APG Dialog (default) and APG AlertDialog
     patterns per WAI-ARIA 1.2. Use 'alertdialog' for dialogs that
     REQUIRE explicit user input (confirm / cancel destructive
     action) — AT signals these as interrupts. Closes the gap from
     PR #64's useConfirm spec §4.1. */
  role?: 'dialog' | 'alertdialog';
  /* Persistent action row pinned below the scrollable body. Use for
     Back/Next/Save/Cancel clusters in forms and multi-step flows. */
  footer?: ReactNode;
  /* Responsive visual presentation. Undefined preserves the historical
     auto behavior: width/max-width-bearing modals stay dialog-shaped on
     mobile; unconstrained modals become bottom sheets below bp.md. */
  presentation?: ModalResponsivePresentation;
}

export const Modal = React.forwardRef<HTMLDivElement, ModalProps>(function Modal(
  {
    open,
    onClose,
    children,
    width,
    title,
    kicker,
    headerless,
    ariaLabel,
    ariaDescribedBy,
    role = 'dialog',
    footer,
    presentation,
    unsafe_className,
    unsafe_style,
    ...rest
  },
  ref,
) {
  const sheetRef = React.useRef<HTMLDivElement | null>(null);
  const restAriaLabel = rest['aria-label'];
  const restAriaLabelledBy = rest['aria-labelledby'];
  const hasRestAccessibleName = Boolean(restAriaLabel || restAriaLabelledBy);
  const setSheetRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      sheetRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );
  /* Tracks pointerdown target for overlay dismissal. MUST be a useRef —
     a re-render between pointerdown and the following click gives the
     click handler a fresh object and a legitimate overlay dismiss is
     silently swallowed. */
  const overlayDownRef = React.useRef<EventTarget | null>(null);
  const titleId = useStableId('jkit-modal-title');

  /* Drag-to-dismiss for mobile bottom-sheet form. The drag handle
     is only visible below bp.md (768px) via CSS; on desktop the
     handle is display:none and the hook is inert. */
  const sheetDrag = useSheetDrag({ sheetRef, onDismiss: onClose });

  /* Warn once per opened instance when the dialog has no accessible
     name — surfaces the a11y regression during QA in any environment. */
  React.useEffect(() => {
    if (open && !title && !ariaLabel && !hasRestAccessibleName) {
      devWarn(
        '[JKit.Modal] opened without `title` or `ariaLabel`; the dialog ' +
          'has no accessible name and screen readers will announce it as ' +
          'empty. Pass a `title` (visible) or `ariaLabel` (non-visible).',
      );
    }
  }, [open, title, ariaLabel, hasRestAccessibleName]);

  /* Stash onClose in a ref so the focus-trap effect calls the latest
     handler without re-running when a consumer passes a fresh inline
     arrow (which would re-capture previouslyFocused on each parent
     render and cause focus to ping-pong inside open modals). */
  const onCloseRef = React.useRef(onClose);
  React.useEffect(() => {
    onCloseRef.current = onClose;
  });

  React.useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    acquireScrollLock();

    /* Mark siblings of the portal host inert so AT (NVDA browse
       mode, VoiceOver with Option-arrows) can't navigate to the
       obscured page. aria-modal="true" on the sheet alone is
       unreliable — major AT combinations ignore it outside the
       single-tab focus loop. inert is the baseline every modern
       modal library (Radix, Headless UI, CMDK) ships.

       Ref-counted via src/kit/internal/modalInert. Stacked modals
       each acquire on every non-modal child; a child stays inert
       until the last outstanding modal releases it. Per-modal
       snapshotting (the previous shape) leaked when an outer
       modal closed before an inner one — its cleanup cleared
       inert on siblings the inner modal still needed. */
    const overlay = (sheetRef.current?.closest('.j-modal-overlay') as HTMLElement | null) ?? null;
    const acquiredInert = acquireSiblingInert(overlay);
    const stackToken = pushModal();

    const sheet = sheetRef.current;
    const firstFocusable = sheet ? tabbables(sheet)[0] : null;
    (firstFocusable ?? sheet)?.focus({ preventScroll: true });

    const onKey = (e: KeyboardEvent) => {
      /* Only the topmost Modal handles keys. Two Modals attaching
         separate document-level listeners can't cooperate via
         stopPropagation alone (both fire on the same dispatch
         round). The modalStack registry pins Escape and Tab
         handling to whichever Modal was pushed last. See
         docs/DEFERRED.md → "Modal stacking". */
      if (!isTopmost(stackToken)) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (e.key !== 'Tab' || !sheet) return;
      const modalOwnedPopovers = Array.from(
        document.querySelectorAll<HTMLElement>('.j-popover[data-modal-popover="true"]'),
      );
      const focusables = [
        ...tabbables(sheet),
        ...modalOwnedPopovers.flatMap((popover) => tabbables(popover)),
      ];
      const active = document.activeElement;
      const activeIsInsideModalOwnedPopover = modalOwnedPopovers.some((popover) =>
        popover.contains(active),
      );
      const activeIsInsideTrap = sheet.contains(active) || activeIsInsideModalOwnedPopover;
      if (!focusables.length) {
        if (activeIsInsideModalOwnedPopover) return;
        e.preventDefault();
        sheet.focus({ preventScroll: true });
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      /* preventScroll on the wrap targets too — a long modal
         body otherwise scrolls unexpectedly when Tab wraps. */
      if (e.shiftKey && (active === first || !activeIsInsideTrap)) {
        e.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!e.shiftKey && (active === last || !activeIsInsideTrap)) {
        e.preventDefault();
        first.focus({ preventScroll: true });
      }
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
      popModal(stackToken);
      releaseScrollLock();
      releaseSiblingInert(acquiredInert);
      /* Restore focus only if the opener is still in the live DOM.
         A common case where it isn't: a route change unmounted the
         trigger while the modal was open. Falling back to calling
         .focus() on a detached node silently lands focus on <body>,
         which is a dead end for keyboard users. Skip it in that
         case; the next meaningful focus stop comes from the route
         the user navigated into. */
      if (
        previouslyFocused &&
        typeof previouslyFocused.focus === 'function' &&
        document.contains(previouslyFocused)
      ) {
        try {
          previouslyFocused.focus({ preventScroll: true });
        } catch {
          /* noop — some elements refuse focus in certain states. */
        }
      }
    };
  }, [open]);

  /* Audit #4 (2026-05-16): the audit recommended deferring the portal
     target lookup via a post-mount `mounted` flag. That cleanly fixes
     the narrow case where adopters render `<Modal open>` SSR-side and
     the server-rendered HTML (null) diverges from the first hydrated
     client render (portal). HOWEVER, deferring breaks the Modal's
     focus-trap contract: tests + adopter code synchronously inspect
     the dialog after render to drive focus management, and a deferred
     portal would leave them probing nothing on the first tick.
     Adopters rendering `<Modal open>` SSR-side should wrap the modal in
     a client-only boundary (`'use client'` + dynamic import with
     `ssr: false`) until the kit ships a separate `ModalAfterMount` API.
     The 'use client' directive on this file already nudges adopters
     toward client-component placement. */
  if (!open || typeof document === 'undefined') return null;

  const labelledById = title ? titleId : undefined;

  const onOverlayPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    overlayDownRef.current = e.target;
  };
  const onOverlayClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (overlayDownRef.current !== e.currentTarget) return;
    onClose?.();
  };

  /* width accepts either a number (treated as px) or any valid CSS
     length/percentage string ("50vw", "80%", "min(640px, 90vw)").
     Previously anything non-numeric went through Number() → NaN and
     was silently dropped — consumers passing "50vw" saw their
     intended width ignored with no feedback. Now numbers suffix
     with "px"; strings pass through verbatim. */
  let resolvedMaxWidth: string | undefined;
  if (typeof width === 'number' && Number.isFinite(width)) {
    resolvedMaxWidth = `${width}px`;
  } else if (typeof width === 'string' && width.trim() !== '') {
    resolvedMaxWidth = width;
  }
  const baseSheetStyle: CSSProperties | undefined = resolvedMaxWidth
    ? { maxWidth: resolvedMaxWidth }
    : undefined;
  const sheetStyle: CSSProperties | undefined = unsafe_style
    ? { ...baseSheetStyle, ...unsafe_style }
    : baseSheetStyle;
  const restProps = pickAllowed(rest as Record<string, unknown>) as HTMLAttributes<HTMLDivElement>;
  const sheetA11yProps: HTMLAttributes<HTMLDivElement> = { ...restProps };
  if (labelledById) {
    sheetA11yProps['aria-labelledby'] = labelledById;
    delete sheetA11yProps['aria-label'];
  } else if (ariaLabel) {
    sheetA11yProps['aria-label'] = ariaLabel;
  }
  if (ariaDescribedBy) {
    sheetA11yProps['aria-describedby'] = ariaDescribedBy;
  }
  const hasEffectiveMaxWidth =
    Boolean(resolvedMaxWidth) ||
    (unsafe_style != null && (unsafe_style.maxWidth != null || unsafe_style.maxInlineSize != null));
  const mobilePresentation =
    typeof presentation === 'string'
      ? presentation
      : (presentation?.mobile ?? (hasEffectiveMaxWidth ? 'dialog' : 'bottom-sheet'));
  const desktopPresentation: 'dialog' =
    typeof presentation === 'object' ? (presentation.desktop ?? 'dialog') : 'dialog';

  const dialog = (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- keyboard dismiss is handled via document-level Escape listener, not on this element
    <div
      className={cx('j-modal-overlay', !onClose && 'j-modal-nondismissible')}
      data-mobile-presentation={mobilePresentation}
      data-desktop-presentation={desktopPresentation}
      onPointerDown={onOverlayPointerDown}
      onClick={onOverlayClick}
    >
      <div
        ref={setSheetRef}
        {...sheetA11yProps}
        className={cx('j-modal-sheet', unsafe_className)}
        data-mobile-presentation={mobilePresentation}
        data-desktop-presentation={desktopPresentation}
        /* No onClick={e.stopPropagation()} here: the overlay handler
           already checks e.target !== e.currentTarget and the
           pointerdown target, so a click inside the sheet never
           reaches the dismiss branch. stopPropagation would also
           kill popover-style consumers inside the modal that listen
           on document for outside-click. */
        style={sheetStyle}
        role={role}
        aria-modal="true"
        tabIndex={-1}
      >
        {/* Drag handle — visible on mobile bottom-sheet only (CSS
            hides on desktop). Only rendered when dismissible (onClose
            defined) — non-dismissible modals must not be swipeable. */}
        {onClose && (
          <div
            className="j-modal-drag-handle"
            onPointerDown={sheetDrag.onPointerDown}
            onPointerMove={sheetDrag.onPointerMove}
            onPointerUp={sheetDrag.onPointerUp}
            aria-hidden="true"
          >
            <div className="j-modal-drag-pill" />
          </div>
        )}
        {/* Header + close-button placement:
            - Header renders when not headerless AND (title || kicker).
            - When a header is present, the close button lives INSIDE
              the header so the .j-modal-header flex container's
              justify-content: space-between right-aligns it next to
              the title (the v4 layout). Putting the button as a sibling
              instead — which the prior "decouple" refactor did — left
              it as an orphan block element flowing below the title bar
              with no right-alignment.
            - When there's no header (headerless OR neither title nor
              kicker), the close button is rendered as a sibling with
              .j-modal-close-float so it anchors absolutely to the
              top-right corner of the sheet. */}
        {!headerless && (title || kicker) && (
          <div className="j-modal-header">
            <div>
              {kicker && (
                <Eyebrow
                  tone="brand"
                  // eslint-disable-next-line @atelierpx/jacquard/no-experimental-without-prefix -- internal layout spacing on the kicker; Modal owns this margin
                  unsafe_style={{ marginBlockEnd: 'var(--sp-xs)' }}
                >
                  {kicker}
                </Eyebrow>
              )}
              {title && (
                <Title level="heading">
                  <span id={titleId}>{title}</span>
                </Title>
              )}
            </div>
            {onClose && (
              <button
                type="button"
                className="j-modal-close"
                onClick={onClose}
                aria-label="Close"
                title="Close"
              >
                <JIcon intent="close" size={14} aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        {onClose && (headerless || (!title && !kicker)) && (
          <button
            type="button"
            className="j-modal-close j-modal-close-float"
            onClick={onClose}
            aria-label="Close"
            title="Close"
          >
            <JIcon intent="close" size={14} aria-hidden="true" />
          </button>
        )}
        <div className="j-modal-body">{children}</div>
        {footer != null && footer !== false && <div className="j-modal-footer">{footer}</div>}
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
});
